// src/hooks/useFirestoreExtensions.js
// ============================================================
// New hooks and helpers for:
//   1. Employee Active/Inactive Status (attendance-aware)
//   2. Holidays (admin-defined, auto-reflects in attendance)
//   3. Shift Management (admin/lead can define & assign shifts)
// ============================================================

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config.js";
import { getTodayStr } from "./useFirestore.js";
import { isPresent, isWorking, isCheckedOut } from "../utils/attendanceStatus.js";

// ────────────────────────────────────────────────────────────
// HOLIDAY HOOKS
// ────────────────────────────────────────────────────────────

/**
 * useHolidays — real-time listener on the `holidays` collection.
 * Returns all holidays, sorted by date ascending.
 */
export function useHolidays() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "holidays"), orderBy("date", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useHolidays]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * useHolidaysInMonth — holidays for a specific year-month.
 */
export function useHolidaysInMonth(year, month) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!year || !month) {
      setData([]);
      return;
    }
    const mm = String(month).padStart(2, "0");
    const from = `${year}-${mm}-01`;
    const to = `${year}-${mm}-31`;

    const q = query(
      collection(db, "holidays"),
      where("date", ">=", from),
      where("date", "<=", to),
      orderBy("date", "asc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [year, month]);

  return { data };
}

/**
 * isHoliday — pure helper. Given a date string and holiday list,
 * returns the holiday object or null.
 */
export function isHoliday(dateStr, holidays) {
  return holidays.find((h) => h.date === dateStr) || null;
}

// ── Holiday write helpers ──────────────────────────────────

/**
 * createHoliday — add a holiday with audit trail.
 * @param {{ name, date, type, userProfile }} holiday
 */
export async function createHoliday({ name, date, type = "public", userProfile = null }) {
  // Validate: date must be strictly after today
  const today = getTodayStr();
  if (!date || date <= today) {
    throw new Error(
      "Holiday date must be at least 1 day ahead. Today and past dates are not allowed.",
    );
  }

  // Prevent duplicate dates
  const existing = await getDocs(
    query(collection(db, "holidays"), where("date", "==", date)),
  );
  if (!existing.empty)
    throw new Error("A holiday already exists on this date.");

  const auditInfo = {
    createdBy: userProfile?.uid || "system",
    createdByName: userProfile?.displayName || userProfile?.name || "Admin/HR",
    createdByRole: userProfile?.role || "admin",
    createdByEmail: userProfile?.email || "",
  };

  const docRef = await addDoc(collection(db, "holidays"), {
    name,
    date, // "YYYY-MM-DD"
    type, // "public" | "optional" | "restricted"
    ...auditInfo,
    updatedBy: auditInfo.createdBy,
    updatedByName: auditInfo.createdByName,
    updatedByRole: auditInfo.createdByRole,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  try {
    await addDoc(collection(db, "auditLogs"), {
      module: "Holidays",
      action: "CREATE_HOLIDAY",
      targetId: docRef.id,
      details: { name, date, type },
      performedBy: auditInfo.createdBy,
      performedByName: auditInfo.createdByName,
      performedByRole: auditInfo.createdByRole,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.error("[createHoliday] audit log:", err);
  }

  return docRef;
}

export async function updateHoliday(holidayId, updates, userProfile = null) {
  const auditInfo = {
    updatedBy: userProfile?.uid || "system",
    updatedByName: userProfile?.displayName || userProfile?.name || "Admin/HR",
    updatedByRole: userProfile?.role || "admin",
    updatedByEmail: userProfile?.email || "",
  };

  await updateDoc(doc(db, "holidays", holidayId), {
    ...updates,
    ...auditInfo,
    updatedAt: serverTimestamp(),
  });

  try {
    await addDoc(collection(db, "auditLogs"), {
      module: "Holidays",
      action: "UPDATE_HOLIDAY",
      targetId: holidayId,
      details: updates,
      performedBy: auditInfo.updatedBy,
      performedByName: auditInfo.updatedByName,
      performedByRole: auditInfo.updatedByRole,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.error("[updateHoliday] audit log:", err);
  }
}

export async function deleteHoliday(holidayId, userProfile = null) {
  const auditInfo = {
    performedBy: userProfile?.uid || "system",
    performedByName: userProfile?.displayName || userProfile?.name || "Admin/HR",
    performedByRole: userProfile?.role || "admin",
  };

  await deleteDoc(doc(db, "holidays", holidayId));

  try {
    await addDoc(collection(db, "auditLogs"), {
      module: "Holidays",
      action: "DELETE_HOLIDAY",
      targetId: holidayId,
      performedBy: auditInfo.performedBy,
      performedByName: auditInfo.performedByName,
      performedByRole: auditInfo.performedByRole,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.error("[deleteHoliday] audit log:", err);
  }
}

/**
 * useUpcomingHolidays — real-time listener returning only future holidays
 * (strictly after today), sorted by date ascending.
 */
export function useUpcomingHolidays() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = getTodayStr();
    const q = query(
      collection(db, "holidays"),
      where("date", ">", today),
      orderBy("date", "asc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useUpcomingHolidays]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

// ────────────────────────────────────────────────────────────
// SHIFT HOOKS
// ────────────────────────────────────────────────────────────

/**
 * useShifts — real-time listener on the `shifts` collection.
 */
export function useShifts() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = collection(db, "shifts");
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort by sortOrder, then by name
        list.sort((a, b) => {
          const o1 = a.sortOrder !== undefined ? a.sortOrder : 999;
          const o2 = b.sortOrder !== undefined ? b.sortOrder : 999;
          if (o1 !== o2) return o1 - o2;
          return (a.name || "").localeCompare(b.name || "");
        });
        setData(list);
        setLoading(false);
      },
      (err) => {
        console.error("[useShifts]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * useEmployeeShift — get the current APPROVED shift assigned to a specific employee.
 */
export function useEmployeeShift(userId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setData(null);
      setLoading(false);
      return;
    }

    // We look for an APPROVED shift assignment for this user
    const q = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", userId),
      where("status", "in", ["APPROVED", "Approved", "approved"]),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!snap.empty) {
          const doc = snap.docs[0];
          setData({ id: doc.id, ...doc.data() });
        } else {
          setData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("[useEmployeeShift]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, [userId]);

  return { data, loading };
}

/**
 * useAllShiftAssignments — all assignments (admin view).
 */
export function useAllShiftAssignments() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "shiftAssignments"),
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useAllShiftAssignments]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * usePendingShiftRequests — listener for PENDING_ADMIN requests.
 */
export function usePendingShiftRequests() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "shiftAssignments"),
      where("status", "in", ["PENDING_ADMIN", "Pending Admin", "PENDING ADMIN", "pending_admin"]),
    );

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const t1 = a.createdAt?.toMillis?.() ?? 0;
        const t2 = b.createdAt?.toMillis?.() ?? 0;
        return t2 - t1; // desc
      });
      setData(docs);
      setLoading(false);
    }, (err) => {
      console.error("[usePendingShiftRequests]", err);
      setLoading(false);
    });
    return unsub;
  }, []);

  return { data, loading };
}

// ── Shift write helpers ────────────────────────────────────

/**
 * createShift — define a new shift.
 * @param {{ name, startTime, endTime, type, description, createdBy }} shift
 * type: "morning" | "evening" | "night" | "custom"
 */
export async function createShift({
  name,
  startTime,
  endTime,
  type = "custom",
  description = "",
  createdBy,
}) {
  // Duplicate timing check (same startTime + endTime)
  const existingSnap = await getDocs(collection(db, "shifts"));
  const duplicate = existingSnap.docs.some(
    (d) => d.data().startTime === startTime && d.data().endTime === endTime,
  );
  if (duplicate) {
    throw new Error("Shift already exists for selected timings.");
  }

  await addDoc(collection(db, "shifts"), {
    name,
    startTime, // "HH:MM" 24h
    endTime, // "HH:MM" 24h
    type,
    description,
    createdBy,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateShift(shiftId, updates) {
  await updateDoc(doc(db, "shifts", shiftId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteShift(shiftId) {
  await deleteDoc(doc(db, "shifts", shiftId));
}

// ── Standard Shifts ─────────────────────────────────────────
const STANDARD_SHIFTS = [
  {
    id: "general-shift",
    name: "General Shift",
    startTime: "09:00",
    endTime: "18:00",
    type: "GENERAL",
    sortOrder: 2,
  },
  {
    id: "morning-shift",
    name: "Morning Shift",
    startTime: "06:00",
    endTime: "15:00",
    type: "MORNING",
    sortOrder: 1,
  },
  {
    id: "evening-shift",
    name: "Evening Shift",
    startTime: "14:00",
    endTime: "23:00",
    type: "EVENING",
    sortOrder: 3,
  },
  {
    id: "night-shift",
    name: "Night Shift",
    startTime: "22:00",
    endTime: "07:00",
    type: "NIGHT",
    sortOrder: 4,
  },
];

const STANDARD_IDS = new Set(STANDARD_SHIFTS.map((s) => s.id));

/**
 * seedStandardShifts — called once on Admin login.
 * Deletes any non-standard shifts and upserts the 4 canonical shifts.
 * Safe to call multiple times (idempotent).
 */
export async function seedStandardShifts(createdBy) {
  const snap = await getDocs(collection(db, "shifts"));
  const batch = writeBatch(db);

  // Delete shifts that are NOT one of the 4 standard IDs
  snap.docs.forEach((d) => {
    if (!STANDARD_IDS.has(d.id)) {
      batch.delete(doc(db, "shifts", d.id));
    }
  });

  // Upsert the 4 standard shifts with fixed document IDs
  STANDARD_SHIFTS.forEach(({ id, ...data }) => {
    batch.set(
      doc(db, "shifts", id),
      {
        ...data,
        isActive: true,
        createdBy: createdBy || "system",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  await batch.commit();

  // Correct any spelling mistakes of "General Shift" in shiftAssignments
  try {
    const assignmentsSnap = await getDocs(collection(db, "shiftAssignments"));
    const assignmentBatch = writeBatch(db);
    let hasAssignmentUpdates = false;

    assignmentsSnap.docs.forEach((d) => {
      const data = d.data();
      const sName = data.shiftName || "";
      const sNameLower = sName.toLowerCase();
      const sStart = data.shiftStart || "";
      const sEnd = data.shiftEnd || "";
      
      const isMisspelledGeneral = sNameLower.startsWith("gen") && !sNameLower.startsWith("general");
      const isGeneralHoursButWrongName = (sStart === "09:00" && sEnd === "18:00" && sName !== "General Shift" && sName !== "Admin Working Hours");

      if (isMisspelledGeneral || isGeneralHoursButWrongName) {
        assignmentBatch.update(doc(db, "shiftAssignments", d.id), {
          shiftName: "General Shift",
          updatedAt: serverTimestamp(),
        });
        hasAssignmentUpdates = true;
      }
    });

    if (hasAssignmentUpdates) {
      await assignmentBatch.commit();
      console.log("Successfully corrected shift names in shiftAssignments.");
    }
  } catch (err) {
    console.warn("Failed to correct shift assignments in migration:", err);
  }
}

const cleanShiftNameForDb = (name, start, end) => {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.startsWith("gen") && !lower.startsWith("general")) {
    return "General Shift";
  }
  if (start && end) {
    const sClean = start.replace(/\s*[AP]M\s*/gi, "").trim();
    const eClean = end.replace(/\s*[AP]M\s*/gi, "").trim();
    if (
      (sClean === "09:00" || sClean === "9:00") &&
      (eClean === "18:00" || eClean === "6:00" || eClean === "06:00") &&
      name !== "Admin Working Hours"
    ) {
      return "General Shift";
    }
  }
  return name;
};

/**
 * assignShift — assign a shift to an employee.
 * status: PENDING_ADMIN | APPROVED | REJECTED
 */
export async function assignShift({
  userId,
  employeeId,
  employeeName,
  shiftId,
  shiftName,
  shiftStart,
  shiftEnd,
  assignedBy,
  assignedByRole,
  teamLeadId,
  status = "PENDING_ADMIN",
  approvedBy = null,
  reviewedBy = null,
}) {
  const batch = writeBatch(db);
  const newAssignmentRef = doc(collection(db, "shiftAssignments"));

  const payload = {
    userId: userId || "",
    employeeId: employeeId || "",
    employeeName: employeeName || "",
    shiftId: shiftId || "",
    shiftName: cleanShiftNameForDb(shiftName || "", shiftStart, shiftEnd),
    shiftStart: shiftStart || "",
    shiftEnd: shiftEnd || "",
    assignedBy: assignedBy || "",
    assignedByRole: assignedByRole || "",
    teamLeadId: teamLeadId || null,
    status: status || "PENDING_ADMIN",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    assignedAt: serverTimestamp(),
    approvedBy: approvedBy || (status === "APPROVED" ? assignedBy : ""),
    reviewedBy: reviewedBy || (status === "APPROVED" ? assignedBy : ""),
    reviewedAt: status === "APPROVED" ? serverTimestamp() : null,
  };

  // If approved, atomically delete all other assignments for this user
  if (status?.toUpperCase() === "APPROVED") {
    const assignmentsQuery = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", userId || "")
    );
    const assignmentsSnap = await getDocs(assignmentsQuery);
    assignmentsSnap.forEach((d) => {
      batch.delete(d.ref);
    });
  } else if (status?.toUpperCase() === "PENDING_ADMIN" || status?.toUpperCase() === "PENDING ADMIN") {
    const existingQuery = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", userId || ""),
      where("status", "in", ["APPROVED", "Approved", "approved", "PENDING_ADMIN", "Pending Admin", "PENDING ADMIN", "pending_admin"])
    );
    const existingSnap = await getDocs(existingQuery);
    if (!existingSnap.empty) {
      throw new Error("Employee already has an active or pending shift assignment.");
    }
  }

  batch.set(newAssignmentRef, payload);
  await batch.commit();
}

/**
 * reviewShiftAssignment — Admin action to approve/reject.
 */
export async function reviewShiftAssignment(assignmentId, status, reviewedBy, approvedBy = null) {
  const ref = doc(db, "shiftAssignments", assignmentId);
  const requestSnap = await getDoc(ref);
  if (!requestSnap.exists()) {
    throw new Error("Request document not found.");
  }
  const requestData = requestSnap.data();
  const employeeUid = requestData.userId || "";

  const batch = writeBatch(db);

  if (status === "APPROVED") {
    // 1. Update target request to APPROVED and fill structure completely
    batch.update(ref, {
      employeeId: requestData.employeeId || "",
      shiftId: requestData.shiftId || "",
      shiftName: requestData.shiftName || "",
      assignedBy: requestData.assignedBy || "",
      status: "APPROVED",
      reviewedBy: reviewedBy || "Admin",
      approvedBy: approvedBy || reviewedBy || "Admin",
      reviewedAt: serverTimestamp(),
      assignedAt: requestData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // 2. Delete all other assignments for this user
    const assignmentsQuery = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", employeeUid)
    );
    const assignmentsSnap = await getDocs(assignmentsQuery);
    assignmentsSnap.forEach((d) => {
      if (d.id !== assignmentId) {
        batch.delete(d.ref);
      }
    });
  } else {
    // Just update the status and review details
    batch.update(ref, {
      employeeId: requestData.employeeId || "",
      shiftId: requestData.shiftId || "",
      shiftName: requestData.shiftName || "",
      assignedBy: requestData.assignedBy || "",
      status: status || "REJECTED",
      reviewedBy: reviewedBy || "Admin",
      approvedBy: approvedBy || reviewedBy || "Admin",
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
}

export async function removeShiftAssignment(assignmentId) {
  await deleteDoc(doc(db, "shiftAssignments", assignmentId));
}

// ────────────────────────────────────────────────────────────
// EMPLOYEE STATUS LOGIC
// ────────────────────────────────────────────────────────────

/**
 * resolveEmployeeStatus — pure function.
 * Given an employee, their attendance record for today, approved leave
 * requests, and today's holiday, returns:
 *   "active"   — checked in / working
 *   "on_leave" — approved leave covers today
 *   "holiday"  — today is a public holiday
 *   "absent"   — none of the above, should have been working
 *   "weekend"  — today is Sat/Sun
 *
 * @param {object} employee   - user doc
 * @param {object|null} att   - today's attendance doc or null
 * @param {object[]} leaves   - all approved leave requests for this employee
 * @param {object|null} holiday - today's holiday doc or null
 * @param {string} todayStr   - "YYYY-MM-DD"
 * @returns {{ status, label, color }}
 */
export function resolveEmployeeStatus(
  employee,
  att,
  leaves,
  holiday,
  todayStr,
) {
  // 1. Weekend check
  const dayOfWeek = new Date(todayStr + "T00:00:00").getDay(); // 0=Sun,6=Sat
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return { status: "weekend", label: "Weekend", color: "gray" };
  }

  // 2. Holiday check
  if (holiday) {
    return { status: "holiday", label: "Holiday", color: "purple" };
  }

  // 3. Attendance check (Priority: Attendance marked -> Count as Present)
  if (isPresent(att)) {
    if (isWorking(att)) {
      // Check if currently on a break
      const activeBreak = (att.breaks || []).find((b) => !b.endTime);
      if (activeBreak) {
        const breakLabel =
          activeBreak.breakType === "lunch"
            ? "On Lunch Break"
            : activeBreak.breakType === "tea1"
              ? "On Tea Break 1"
              : "On Tea Break 2";
        return { status: "on_break", label: breakLabel, color: "amber" };
      }
      return { status: "active", label: "Working", color: "green" };
    }

    if (isCheckedOut(att)) {
      if (att.status === "AUTO-CHECKED-OUT" || att.status === "auto-checked-out") {
        return { status: "auto-checked-out", label: "Auto Checked-Out", color: "blue" };
      }
      return { status: "completed", label: "Checked-Out", color: "blue" };
    }
  }

  // 4. Leave request checks (Priority: Approved -> Pending -> Rejected/Cancelled)
  const userLeaves = leaves.filter(
    (l) => l.fromDate <= todayStr && l.toDate >= todayStr
  );

  const onLeave = userLeaves.find((l) => l.status === "approved");
  if (onLeave) {
    return { status: "on_leave", label: "On Leave", color: "amber" };
  }

  const pendingLeave = userLeaves.find(
    (l) => l.status === "pending_lead" || l.status === "pending_admin"
  );
  if (pendingLeave) {
    return { status: "pending_leave", label: "Pending Leave", color: "amber" };
  }

  const rejectedLeave = userLeaves.find(
    (l) => l.status === "rejected" || l.status === "cancelled"
  );
  if (rejectedLeave) {
    return { status: "rejected_leave", label: "Rejected/Cancelled Leave", color: "gray" };
  }

  // 5. Default → absent
  return { status: "absent", label: "Absent", color: "red" };
}

/**
 * useEmployeeStatuses — computes status for all employees for today.
 * Returns a map: userId → { status, label, color }
 *
 * @param {object[]} employees  - all employee user docs
 * @param {object[]} todayAtt   - all attendance records for today
 * @param {object[]} allLeaves  - all approved leave requests
 * @param {object[]} holidays   - all holidays
 */
export function useEmployeeStatuses(employees, todayAtt, allLeaves, holidays) {
  const todayStr = getTodayStr();
  const todayHoliday = isHoliday(todayStr, holidays);

  const statusMap = {};

  for (const emp of employees) {
    const att = todayAtt.find((a) => a.userId === emp.uid) || null;
    const empLeaves = allLeaves.filter((l) => l.userId === emp.uid);
    statusMap[emp.uid] = resolveEmployeeStatus(
      emp,
      att,
      empLeaves,
      todayHoliday,
      todayStr,
    );
  }

  return statusMap;
}

// ────────────────────────────────────────────────────────────
// TEAM REMOVAL REQUESTS HOOKS
// Workflow: Team Lead requests removal → Admin approves/rejects
// ────────────────────────────────────────────────────────────

/**
 * usePendingRemovalRequests — real-time listener for all PENDING_ADMIN removal requests.
 * Admin sees these on the Admin Employees page.
 */
export function usePendingRemovalRequests() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simple query: only filter by status to avoid index requirement
    const q = query(
      collection(db, "teamRemovalRequests"),
      where("status", "==", "PENDING_ADMIN"),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Sort client-side to avoid Firestore index errors
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort(
          (a, b) =>
            (b.requestedAt?.toMillis?.() ?? 0) -
            (a.requestedAt?.toMillis?.() ?? 0),
        );
        setData(docs);
        setLoading(false);
      },
      (err) => {
        console.error("[usePendingRemovalRequests]", err.code, err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * createRemovalRequest — Team Lead submits a removal request.
 * @param {{employeeId, employeeUid, employeeName, teamLeadId, teamLeadName, reason}} params
 */
export async function createRemovalRequest({
  employeeId,
  employeeUid,
  employeeName,
  teamLeadId,
  teamLeadName,
  reason = "",
}) {
  await addDoc(collection(db, "teamRemovalRequests"), {
    employeeId,
    employeeUid,
    employeeName,
    teamLeadId,
    teamLeadName,
    reason,
    requestedAt: serverTimestamp(),
    status: "PENDING_ADMIN",
    approvedBy: null,
    approvedAt: null,
    rejectionReason: null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * approveRemovalRequest — Admin approves removal and executes the removal process.
 * This performs the actual employee removal (same as the old direct deletion):
 *   1. Unassign user from team
 *   2. Cancel pending leave requests
 *   3. Unassign open tasks
 * @param {string} requestId - removal request document ID
 * @param {string} adminUid - approving admin's UID
 */
export async function approveRemovalRequest(requestId, adminUid) {
  // Get the request document directly by ID
  const requestRef = doc(db, "teamRemovalRequests", requestId);
  const requestSnap = await getDoc(requestRef);

  if (!requestSnap.exists()) throw new Error("Removal request not found");

  const request = { id: requestSnap.id, ...requestSnap.data() };
  const employeeUid = request.employeeUid;

  // Execute the removal (batch operation - same as before)
  const batch = writeBatch(db);

  // 1. Unassign user from team
  batch.update(doc(db, "users", employeeUid), {
    teamId: null,
    teamLeadId: null,
    managerId: null,
    updatedAt: serverTimestamp(),
  });

  // 2. Cancel their pending leave requests
  const leaveSnap = await getDocs(
    query(
      collection(db, "leaveRequests"),
      where("userId", "==", employeeUid),
      where("status", "in", ["pending_lead", "pending_admin"]),
    ),
  );
  leaveSnap.forEach((d) => {
    batch.update(d.ref, {
      status: "rejected",
      rejectedBy: adminUid,
      rejectedAt: serverTimestamp(),
      rejectedReason: "Employee removed from team by admin",
      updatedAt: serverTimestamp(),
    });
  });

  // 3. Unassign open tasks
  const taskSnap = await getDocs(
    query(
      collection(db, "tasks"),
      where("assignedTo", "==", employeeUid),
      where("status", "in", ["pending", "in_progress", "PENDING", "IN_PROGRESS"]),
    ),
  );
  taskSnap.forEach((d) => {
    batch.update(d.ref, {
      assignedTo: null,
      assignedToName: `[Removed] ${request.employeeName}`,
      updatedAt: serverTimestamp(),
    });
  });

  // 4. Mark the removal request as APPROVED
  batch.update(doc(db, "teamRemovalRequests", requestId), {
    status: "APPROVED",
    approvedBy: adminUid,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

/**
 * rejectRemovalRequest — Admin rejects the removal request.
 * @param {string} requestId
 * @param {string} adminUid - rejecting admin's UID
 * @param {string} rejectionReason - why it was rejected
 */
export async function rejectRemovalRequest(
  requestId,
  adminUid,
  rejectionReason = "",
) {
  await updateDoc(doc(db, "teamRemovalRequests", requestId), {
    status: "REJECTED",
    approvedBy: adminUid,
    approvedAt: serverTimestamp(),
    rejectionReason,
    updatedAt: serverTimestamp(),
  });
}

export function useExtraBreaks(teamId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = collection(db, "extraBreaks");
    if (teamId) {
      q = query(q, where("teamId", "==", teamId));
    }
    const unsub = onSnapshot(
      q,
      (snap) => {
        const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort descending by createdAt
        results.sort((a, b) => {
          const t1 = a.createdAt?.toMillis() || 0;
          const t2 = b.createdAt?.toMillis() || 0;
          return t2 - t1;
        });
        setData(results);
        setLoading(false);
      },
      (err) => {
        console.error("[useExtraBreaks]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [teamId]);

  return { data, loading };
}

// ────────────────────────────────────────────────────────────
// AUDIT TRAIL LOGGING
// ────────────────────────────────────────────────────────────

export async function logAuditAction({
  user,
  userId,
  role,
  action,
  entity,
  entityId,
  previousValue = null,
  updatedValue = null,
  ipAddress = "127.0.0.1",
  device = navigator.userAgent || "Web Browser",
}) {
  try {
    await addDoc(collection(db, "auditLogs"), {
      userId: userId || user?.uid || user?.id || "system",
      userName: user?.displayName || user?.fullName || user?.email || "System User",
      role: role || user?.role || "user",
      action,
      entity,
      entityId: entityId || "N/A",
      previousValue: previousValue ? JSON.stringify(previousValue) : null,
      updatedValue: updatedValue ? JSON.stringify(updatedValue) : null,
      ipAddress,
      device: device.substring(0, 100),
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
    });
  } catch (err) {
    console.warn("[logAuditAction] Failed to record audit log:", err);
  }
}

export function useAuditLogs(limitCount = 100) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "auditLogs"),
      orderBy("timestamp", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })).slice(0, limitCount));
        setLoading(false);
      },
      (err) => {
        console.error("[useAuditLogs]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [limitCount]);

  return { data, loading };
}

// ────────────────────────────────────────────────────────────
// GEO-FENCING & LOCATION VERIFICATION
// ────────────────────────────────────────────────────────────

export function calculateDistanceMeters(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
  const R = 6371e3; // metres
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(R * c);
}

export function verifyGeofence(userLat, userLng, locations = []) {
  if (!userLat || !userLng || !locations.length) {
    return { isWithinZone: true, matchedLocation: null, distance: 0 };
  }
  for (const loc of locations) {
    if (loc.latitude && loc.longitude) {
      const distance = calculateDistanceMeters(userLat, userLng, loc.latitude, loc.longitude);
      const radius = loc.radiusMeters || 500;
      if (distance <= radius) {
        return { isWithinZone: true, matchedLocation: loc, distance };
      }
    }
  }
  return { isWithinZone: false, matchedLocation: null, distance: Infinity };
}

export function useWorkLocations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "workLocations"),
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useWorkLocations]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return { data, loading };
}

export async function createWorkLocation(locationData, user) {
  const docRef = await addDoc(collection(db, "workLocations"), {
    ...locationData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await logAuditAction({
    user,
    action: "CREATE_WORK_LOCATION",
    entity: "workLocations",
    entityId: docRef.id,
    updatedValue: locationData,
  });
  return docRef.id;
}

export async function updateWorkLocation(id, locationData, user) {
  await updateDoc(doc(db, "workLocations", id), {
    ...locationData,
    updatedAt: serverTimestamp(),
  });
  await logAuditAction({
    user,
    action: "UPDATE_WORK_LOCATION",
    entity: "workLocations",
    entityId: id,
    updatedValue: locationData,
  });
}

export async function deleteWorkLocation(id, user) {
  await deleteDoc(doc(db, "workLocations", id));
  await logAuditAction({
    user,
    action: "DELETE_WORK_LOCATION",
    entity: "workLocations",
    entityId: id,
  });
}

// ────────────────────────────────────────────────────────────
// ATTENDANCE DEVICES & POLICIES
// ────────────────────────────────────────────────────────────

export function useAttendanceDevices() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "attendanceDevices"),
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useAttendanceDevices]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return { data, loading };
}

export async function createAttendanceDevice(deviceData, user) {
  const docRef = await addDoc(collection(db, "attendanceDevices"), {
    ...deviceData,
    status: "active",
    createdAt: serverTimestamp(),
  });
  await logAuditAction({
    user,
    action: "CREATE_ATTENDANCE_DEVICE",
    entity: "attendanceDevices",
    entityId: docRef.id,
    updatedValue: deviceData,
  });
  return docRef.id;
}

export async function deleteAttendanceDevice(id, user) {
  await deleteDoc(doc(db, "attendanceDevices", id));
  await logAuditAction({
    user,
    action: "DELETE_ATTENDANCE_DEVICE",
    entity: "attendanceDevices",
    entityId: id,
  });
}

// ────────────────────────────────────────────────────────────
// ATTENDANCE CORRECTIONS (REGULARIZATION)
// ────────────────────────────────────────────────────────────

export function useAttendanceCorrections(userId = null, teamId = null, role = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = collection(db, "attendanceCorrections");
    if (role === "employee" && userId) {
      q = query(q, where("userId", "==", userId));
    } else if (role === "lead" && teamId) {
      q = query(q, where("teamId", "==", teamId));
    }
    const unsub = onSnapshot(
      q,
      (snap) => {
        const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        results.sort((a, b) => {
          const t1 = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const t2 = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return t2 - t1;
        });
        setData(results);
        setLoading(false);
      },
      (err) => {
        console.error("[useAttendanceCorrections]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [userId, teamId, role]);

  return { data, loading };
}

export async function createAttendanceCorrection(requestData, user) {
  const docRef = await addDoc(collection(db, "attendanceCorrections"), {
    ...requestData,
    status: "pending_lead",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Create notification for Lead/HR
  await addDoc(collection(db, "notifications"), {
    recipientId: requestData.teamLeadId || "admin",
    title: "Attendance Correction Requested",
    message: `${requestData.employeeName || user?.displayName} requested regularisation for ${requestData.date}`,
    type: "correction_request",
    read: false,
    createdAt: serverTimestamp(),
  });

  await logAuditAction({
    user,
    action: "REQUEST_ATTENDANCE_CORRECTION",
    entity: "attendanceCorrections",
    entityId: docRef.id,
    updatedValue: requestData,
  });

  return docRef.id;
}

export async function reviewAttendanceCorrection(correctionId, action, reviewer, newStatus = null) {
  const ref = doc(db, "attendanceCorrections", correctionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Correction request not found.");

  const current = snap.data();
  let updatedStatus = current.status;

  if (action === "approve") {
    if (current.status === "pending_lead") {
      updatedStatus = "pending_hr";
    } else if (current.status === "pending_hr" || current.status === "pending_admin") {
      updatedStatus = "approved";
    } else {
      updatedStatus = "approved";
    }
  } else if (action === "reject") {
    updatedStatus = "rejected";
  }

  await updateDoc(ref, {
    status: updatedStatus,
    reviewedBy: reviewer?.displayName || reviewer?.email || "Reviewer",
    reviewerRole: reviewer?.role || "lead",
    updatedAt: serverTimestamp(),
  });

  // If fully approved, update the attendance record directly
  if (updatedStatus === "approved") {
    const attId = `${current.userId}_${current.date}`;
    const attRef = doc(db, "attendance", attId);
    const attSnap = await getDoc(attRef);

    const updatePayload = {
      status: current.requestedStatus || "present",
      checkIn: current.requestedClockIn || current.originalClockIn || "09:00",
      checkOut: current.requestedClockOut || current.originalClockOut || "18:00",
      isRegularized: true,
      regularizedBy: reviewer?.displayName || "HR Admin",
      updatedAt: serverTimestamp(),
    };

    if (attSnap.exists()) {
      await updateDoc(attRef, updatePayload);
    } else {
      await setDoc(attRef, {
        id: attId,
        userId: current.userId,
        employeeId: current.employeeId || "",
        displayName: current.employeeName || "",
        department: current.department || "",
        date: current.date,
        ...updatePayload,
        createdAt: serverTimestamp(),
      });
    }
  }

  // Notify Employee
  await addDoc(collection(db, "notifications"), {
    recipientId: current.userId,
    title: `Attendance Regularisation ${updatedStatus.toUpperCase()}`,
    message: `Your attendance correction request for ${current.date} was ${updatedStatus}.`,
    type: "correction_status",
    read: false,
    createdAt: serverTimestamp(),
  });

  await logAuditAction({
    user: reviewer,
    action: `REVIEW_CORRECTION_${action.toUpperCase()}`,
    entity: "attendanceCorrections",
    entityId: correctionId,
    previousValue: { status: current.status },
    updatedValue: { status: updatedStatus },
  });
}

// ────────────────────────────────────────────────────────────
// SHIFT SWAPS
// ────────────────────────────────────────────────────────────

export function useShiftSwaps(userId = null, teamId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = collection(db, "shiftSwaps");
    if (userId) {
      q = query(q, where("requesterId", "==", userId));
    }
    const unsub = onSnapshot(
      q,
      (snap) => {
        const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        results.sort((a, b) => {
          const t1 = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const t2 = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return t2 - t1;
        });
        setData(results);
        setLoading(false);
      },
      (err) => {
        console.error("[useShiftSwaps]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [userId, teamId]);

  return { data, loading };
}

export async function createShiftSwapRequest(swapData, user) {
  const docRef = await addDoc(collection(db, "shiftSwaps"), {
    ...swapData,
    status: "pending_lead",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await addDoc(collection(db, "notifications"), {
    recipientId: swapData.targetUserId || swapData.teamLeadId || "lead",
    title: "Shift Swap Request Received",
    message: `${user?.displayName} requested a shift swap for ${swapData.date}`,
    type: "shift_swap",
    read: false,
    createdAt: serverTimestamp(),
  });

  await logAuditAction({
    user,
    action: "REQUEST_SHIFT_SWAP",
    entity: "shiftSwaps",
    entityId: docRef.id,
    updatedValue: swapData,
  });

  return docRef.id;
}

export async function reviewShiftSwap(swapId, action, reviewer) {
  const ref = doc(db, "shiftSwaps", swapId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Shift swap request not found.");

  const current = snap.data();
  const newStatus = action === "approve" ? "approved" : "rejected";

  await updateDoc(ref, {
    status: newStatus,
    reviewedBy: reviewer?.displayName || "Team Lead",
    updatedAt: serverTimestamp(),
  });

  if (newStatus === "approved") {
    // Perform shift swap update in shiftAssignments if records exist
    if (current.requesterAssignmentId && current.targetAssignmentId) {
      await updateDoc(doc(db, "shiftAssignments", current.requesterAssignmentId), {
        shiftId: current.targetShiftId,
        shiftName: current.targetShiftName,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "shiftAssignments", current.targetAssignmentId), {
        shiftId: current.requesterShiftId,
        shiftName: current.requesterShiftName,
        updatedAt: serverTimestamp(),
      });
    }
  }

  await addDoc(collection(db, "notifications"), {
    recipientId: current.requesterId,
    title: `Shift Swap Request ${newStatus.toUpperCase()}`,
    message: `Your shift swap request for ${current.date} has been ${newStatus}.`,
    type: "shift_swap_status",
    read: false,
    createdAt: serverTimestamp(),
  });

  await logAuditAction({
    user: reviewer,
    action: `REVIEW_SHIFT_SWAP_${action.toUpperCase()}`,
    entity: "shiftSwaps",
    entityId: swapId,
    previousValue: { status: current.status },
    updatedValue: { status: newStatus },
  });
}

// ────────────────────────────────────────────────────────────
// OVERTIME REQUESTS
// ────────────────────────────────────────────────────────────

export function useOvertimeRequests(userId = null, teamId = null, role = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = collection(db, "overtimeRequests");
    if (role === "employee" && userId) {
      q = query(q, where("userId", "==", userId));
    }
    const unsub = onSnapshot(
      q,
      (snap) => {
        const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        results.sort((a, b) => {
          const t1 = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const t2 = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return t2 - t1;
        });
        setData(results);
        setLoading(false);
      },
      (err) => {
        console.error("[useOvertimeRequests]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [userId, teamId, role]);

  return { data, loading };
}

export async function createOvertimeRequest(requestData, user) {
  // Estimated cost = extraHours * hourlyRate (default multiplier 1.5x of estimated 250/hr = 375/hr)
  const rate = requestData.hourlyRate || 375;
  const estimatedCost = (Number(requestData.extraHours) || 0) * rate;

  const docRef = await addDoc(collection(db, "overtimeRequests"), {
    ...requestData,
    estimatedCost,
    status: "pending_lead",
    paymentStatus: "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await addDoc(collection(db, "notifications"), {
    recipientId: requestData.teamLeadId || "lead",
    title: "Overtime Request Submitted",
    message: `${user?.displayName || requestData.employeeName} submitted ${requestData.extraHours} hrs overtime for ${requestData.date}`,
    type: "overtime_request",
    read: false,
    createdAt: serverTimestamp(),
  });

  await logAuditAction({
    user,
    action: "REQUEST_OVERTIME",
    entity: "overtimeRequests",
    entityId: docRef.id,
    updatedValue: { ...requestData, estimatedCost },
  });

  return docRef.id;
}

export async function reviewOvertimeRequest(requestId, action, reviewer) {
  const ref = doc(db, "overtimeRequests", requestId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Overtime request not found.");

  const current = snap.data();
  let updatedStatus = current.status;

  if (action === "approve") {
    if (current.status === "pending_lead") {
      updatedStatus = "pending_hr";
    } else {
      updatedStatus = "approved";
    }
  } else {
    updatedStatus = "rejected";
  }

  await updateDoc(ref, {
    status: updatedStatus,
    reviewedBy: reviewer?.displayName || reviewer?.email || "Reviewer",
    paymentStatus: updatedStatus === "approved" ? "approved_for_payroll" : "rejected",
    updatedAt: serverTimestamp(),
  });

  // If approved, update attendance record with overtime hours
  if (updatedStatus === "approved") {
    const attId = `${current.userId}_${current.date}`;
    const attRef = doc(db, "attendance", attId);
    const attSnap = await getDoc(attRef);
    if (attSnap.exists()) {
      await updateDoc(attRef, {
        overtimeHours: (attSnap.data().overtimeHours || 0) + Number(current.extraHours || 0),
        updatedAt: serverTimestamp(),
      });
    }
  }

  await addDoc(collection(db, "notifications"), {
    recipientId: current.userId,
    title: `Overtime Request ${updatedStatus.toUpperCase()}`,
    message: `Your request for ${current.extraHours} hours overtime on ${current.date} has been ${updatedStatus}.`,
    type: "overtime_status",
    read: false,
    createdAt: serverTimestamp(),
  });

  await logAuditAction({
    user: reviewer,
    action: `REVIEW_OVERTIME_${action.toUpperCase()}`,
    entity: "overtimeRequests",
    entityId: requestId,
    previousValue: { status: current.status },
    updatedValue: { status: updatedStatus },
  });
}

